import { JOB_NAMES, QUEUE_NAMES, SCHEDULES } from '@btsis/jobs';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { bulkResultSchema, sortedListRequest } from './common/list.js';
import { paginated } from './common/pagination.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const isoDaySchema = z.iso.date('a day is a real YYYY-MM-DD date');

/** The jobs and queues a filter may name: the job catalogue's, and nothing typed. */
export const JOB_FILTER_NAMES = JOB_NAMES;
export const JOB_QUEUE_NAMES = QUEUE_NAMES;

/**
 * The daily schedules an operator may start by hand, as `@btsis/jobs` declares
 * them. They are a fixed catalogue rather than a listing endpoint: a schedule is
 * code, not a row, so the screen renders what the contract carries and only the
 * verb needs the server.
 */
export const JOB_SCHEDULES = SCHEDULES.map(({ id, job, pattern, tz }) => ({
  id,
  job,
  pattern,
  tz,
})) as readonly { id: string; job: string; pattern: string; tz: string }[];

export const JOB_SCHEDULE_IDS = JOB_SCHEDULES.map((schedule) => schedule.id);

export const JOB_FAILURE_STATUSES = ['open', 'retried', 'dismissed'] as const;

export type JobFailureStatus = (typeof JOB_FAILURE_STATUSES)[number];

/**
 * What a scheduled run's ledger row says; `running` is a row with no outcome
 * yet. `no_bulletin` is a TCMB day that answered 404, not a failure.
 */
export const SCHEDULED_RUN_OUTCOMES = ['succeeded', 'failed', 'no_bulletin', 'running'] as const;

export type ScheduledRunOutcome = (typeof SCHEDULED_RUN_OUTCOMES)[number];

/**
 * A job that failed for good. `payloadIds` is the payload the catalogue
 * accepted — ids, dates and scalars, never a name or an address — and empty for
 * one it refused. `message` is written by the worker from a classification of
 * the error, never copied from it. `retriable` says whether the payload can be
 * asked for again at all; whether it may be is `jobs.manage` and the row being
 * `open`.
 */
export const jobFailureSchema = z.object({
  id: z.uuid(),
  job: z.string(),
  queue: z.string(),
  jobId: z.string(),
  payloadIds: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
  attempts: z.number().int(),
  errorCode: z.string(),
  message: z.string(),
  failedAt: z.string(),
  status: z.enum(JOB_FAILURE_STATUSES),
  resolvedAt: z.string().nullable(),
  resolvedByName: z.string().nullable(),
  retriable: z.boolean(),
});

export type JobFailure = z.infer<typeof jobFailureSchema>;

export const scheduledRunSchema = z.object({
  id: z.uuid(),
  job: z.string(),
  runKey: z.string(),
  jobId: z.string(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  outcome: z.enum(SCHEDULED_RUN_OUTCOMES),
});

export type ScheduledRun = z.infer<typeof scheduledRunSchema>;

/**
 * What starting a schedule by hand came to: the run it asked for, by the date
 * the schedule would have run it for in its own zone, and the key the ledger
 * records that run under. A refusal is an error, never a row here.
 */
export const scheduledJobRunNowSchema = z.object({
  scheduleId: z.string(),
  job: z.string(),
  runDate: z.string(),
  runKey: z.string(),
});

export type ScheduledJobRunNow = z.infer<typeof scheduledJobRunNowSchema>;

/**
 * A verb applied to a selection of failures, answering per row: `ok` with no
 * code for a row it resolved, and `ok` false with the refusal's own reason —
 * `NOT_FOUND`, `JOB_FAILURE_NOT_OPEN`, `JOB_FAILURE_NOT_RETRIABLE` — for one it
 * did not. One row's refusal never fails the others.
 */
export const jobFailureResolutionResultSchema = z.object({
  id: z.uuid(),
  ok: z.boolean(),
  code: z.string().nullable(),
});

export type JobFailureResolutionResult = z.infer<typeof jobFailureResolutionResultSchema>;

export const JOB_FAILURE_RESOLUTIONS = ['retry', 'dismiss'] as const;

export type JobFailureResolution = (typeof JOB_FAILURE_RESOLUTIONS)[number];

export const JOB_FAILURE_SORT_FIELDS = ['failedAt', 'job'] as const;

export const SCHEDULED_RUN_SORT_FIELDS = ['startedAt', 'job'] as const;

const idParams = z.object({ id: z.uuid() });

export const jobContract = {
  failures: oc
    .route({
      method: 'GET',
      path: '/jobs/failures',
      summary: 'Jobs that failed for good, newest first; global scope only',
    })
    .errors(guarded)
    .input(
      sortedListRequest(JOB_FAILURE_SORT_FIELDS, 'failedAt', 'desc')
        .omit({ search: true })
        .extend({
          status: z.enum(JOB_FAILURE_STATUSES).optional(),
          job: z.enum(JOB_FILTER_NAMES).optional(),
          queue: z.enum(JOB_QUEUE_NAMES).optional(),
          failedFrom: isoDaySchema.optional(),
          failedTo: isoDaySchema.optional(),
        }),
    )
    .output(paginated(jobFailureSchema)),

  retryFailure: oc
    .route({
      method: 'POST',
      path: '/jobs/failures/{id}/retry',
      summary: 'Ask for a failed job again, under the id it failed with',
    })
    .errors(guarded)
    .input(idParams)
    .output(jobFailureSchema),

  dismissFailure: oc
    .route({
      method: 'POST',
      path: '/jobs/failures/{id}/dismiss',
      summary: 'Mark a failed job as seen and not to be retried',
    })
    .errors(guarded)
    .input(idParams)
    .output(jobFailureSchema),

  /**
   * The list screen's bulk verb, as `user.setActive` and `campaign.setArchived`
   * are: one call carries the whole selection and answers per row, so a
   * selection mixing failures that can be asked for again with ones that cannot
   * reports exactly which were refused instead of failing wholesale.
   */
  resolveFailures: oc
    .route({
      method: 'POST',
      path: '/jobs/failures/resolution',
      summary: 'Retry or dismiss a selection of failures, answering per row',
    })
    .errors(guarded)
    .input(
      z.object({
        ids: z.array(z.uuid()).min(1).max(100),
        resolution: z.enum(JOB_FAILURE_RESOLUTIONS),
      }),
    )
    .output(bulkResultSchema(jobFailureResolutionResultSchema)),

  /**
   * The third verb docs/decisions/0043-the-worker.md §6 named. It asks for one
   * daily schedule's run for today, under the run key the scheduler itself
   * would have used, so a run started by hand and one started by the clock are
   * the same run and the ledger cannot hold two.
   */
  runSchedule: oc
    .route({
      method: 'POST',
      path: '/jobs/schedules/{id}/run',
      summary: 'Start a scheduled job now, under the run key its schedule would use',
    })
    .errors(guarded)
    .input(z.object({ id: z.string().min(1).max(100) }))
    .output(scheduledJobRunNowSchema),

  runs: oc
    .route({
      method: 'GET',
      path: '/jobs/runs',
      summary: 'The scheduled run ledger, newest first; global scope only',
    })
    .errors(guarded)
    .input(
      sortedListRequest(SCHEDULED_RUN_SORT_FIELDS, 'startedAt', 'desc')
        .omit({ search: true })
        .extend({
          job: z.enum(JOB_FILTER_NAMES).optional(),
          outcome: z.enum(SCHEDULED_RUN_OUTCOMES).optional(),
          startedFrom: isoDaySchema.optional(),
          startedTo: isoDaySchema.optional(),
        }),
    )
    .output(paginated(scheduledRunSchema)),
};
